/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output } from '@angular/core';

import { whenHasSystemRole, whenOfficeView } from '@gh/builder-shared';
import { ContactCustomerType, SystemRoleBit } from '@gh/builder-shared-data';
import { ContextRoute, getContextRouteInitData } from '@gh/common-internal-shared';
import {
  AbstractSnapshotFormComponent, DictionaryItem,
  EnumDictionary,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  UserContext,
  whenAllOf,
  whenCreateMode,
  whenCreateOrEditMode,
  whenOneOf,
} from '@gh/common-shared';
import { AutocompleteLoadMoreEvent, getControlValue } from '@gh/core-ui';

import { assign, negate } from 'lodash';

import { LeadDetailsBusinessService } from '../../../resources/lead-details-business.service';
import { LeadContactDetails, LeadMasterInfo } from '../../../resources/lead.model';
import { sortByStringField } from '@gh/core-util';

@Component({
  selector: 'gh-lead-details-top-form',
  template: require('./lead-top-form.component.html'),
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeadDetailsTopFormComponent extends AbstractSnapshotFormComponent<LeadMasterInfo> implements OnInit {
  @Output() createContact = new EventEmitter<void>();
  @Output() openContact = new EventEmitter<LeadContactDetails>();
  @Output() selectContact = new EventEmitter<LeadContactDetails>();
  customerTypes: EnumDictionary<ContactCustomerType>;

  constructor(private userContext: UserContext,
              private formStateBuilder: FormStateBuilder,
              private businessService: LeadDetailsBusinessService,
              private route: ContextRoute) {
    super();
  }

  ngOnInit(): void {
    assign(this, getContextRouteInitData(this.route));
    super.ngOnInit();
  }

  get sortedCustomerTypes(): any[] {
    return sortByStringField(this.customerTypes.seq(), 'name');
  }

  loadCustomers($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;

    load(this.businessService.findCustomersContactsByName(query));
  }

  loadSalesPersons($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;

    load(this.businessService.findSalesPersonsByName(query));
  }

  onCreateContact(): void {
    this.createContact.emit();
  }

  onOpenContact(): void {
    const customer = getControlValue(this.form.group, 'customer');
    if (customer) {
      this.openContact.emit(customer);
    }
  }

  onSelectCustomer(customer?: LeadContactDetails): void {
    this.selectContact.emit(customer);
  }

  protected buildForm(snapshot: FormStateSnapshot<LeadMasterInfo>): FormState<LeadMasterInfo> {
    return this.formStateBuilder.buildPlain<LeadMasterInfo>({
      snapshot,
      mode: this.mode,
      enabled: whenCreateOrEditMode(),
      children: {
        customer: { required: true, enabled: whenCreateMode() },
        salesPerson: {
          required: true,
          enabled: [
            whenCreateMode(),
            whenOneOf(
              whenHasSystemRole(SystemRoleBit.Corporate),
              whenHasSystemRole(SystemRoleBit.Master),
              whenAllOf(whenOfficeView(), negate(whenHasSystemRole(SystemRoleBit.SalesPerson))),
            ),
          ],
        },
        customerType: { required: true },
      },
    });
  }
}
